/**
 * Client Brands (DWA Internal → Accounts, mirrored under Creative).
 *
 * One client at a time — brands and their sub-brands, with the brand colors the
 * Banner Manager reads as presets. Brands are keyed on the ROT's Community
 * Template, which is what actually determines a site's design; Community Group
 * is kept alongside as the ownership/org dimension.
 *
 * Null colors mean "inherit" (parent brand, then the global banner defaults),
 * so a sub-brand only stores what it genuinely overrides. Site counts come from
 * the registered-site registry, so a brand can show 0 sites while its
 * communities are still in development.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchBrands, createBrand, updateBrand, deleteBrand,
  BRAND_CLIENTS,
  type Brand, type BrandInput, type BrandKind, type BackendClientId,
} from '../services/dashboardApi';
import type { ScorecardTheme } from './ScorecardView';

export interface ClientBrandsProps {
  theme: ScorecardTheme;
}

// Mirrors the banner renderer's fallbacks — what a null color resolves to.
const BANNER_DEFAULTS = { bg_color: '#1a1a1a', text_color: '#ffffff', button_color: '#fec35c' };

const COLOR_FIELDS = [
  { key: 'bg_color', label: 'Background', fallback: BANNER_DEFAULTS.bg_color },
  { key: 'text_color', label: 'Text', fallback: BANNER_DEFAULTS.text_color },
  { key: 'button_color', label: 'Button', fallback: BANNER_DEFAULTS.button_color },
  { key: 'button_hover_color', label: 'Button hover', fallback: '#e5a93f' },
  { key: 'button_text_color', label: 'Button text', fallback: '#111111' },
  { key: 'button_text_hover_color', label: 'Button text hover', fallback: '#111111' },
] as const;

const GRADIENT_TO_DEFAULT = '#3a3a3a';
const GRADIENT_MID_DEFAULT = '#ffffff';

/** Mirrors banner_service._background — gradient only when BOTH stops are set. */
function brandBackground(b: Pick<BrandInput, 'bg_color' | 'bg_gradient_from' | 'bg_gradient_mid' | 'bg_gradient_to' | 'bg_gradient_angle'>): string {
  const flat = b.bg_color || BANNER_DEFAULTS.bg_color;
  if (!b.bg_gradient_from || !b.bg_gradient_to) return flat;
  const angle = ((b.bg_gradient_angle ?? 180) % 360 + 360) % 360;
  const stops = b.bg_gradient_mid
    ? `${b.bg_gradient_from}, ${b.bg_gradient_mid}, ${b.bg_gradient_to}`
    : `${b.bg_gradient_from}, ${b.bg_gradient_to}`;
  return `linear-gradient(${angle}deg, ${stops})`;
}

function emptyBrand(kind: BrandKind, parentId: number | null): BrandInput {
  return {
    name: '', kind, parent_id: parentId,
    template_key: '', group_key: '',
    bg_color: null, text_color: null, button_color: null, button_hover_color: null,
    button_text_color: null, button_text_hover_color: null,
    bg_gradient_from: null, bg_gradient_mid: null, bg_gradient_to: null,
    bg_gradient_angle: null,
    notes: '', sort_order: 0, is_active: true,
  };
}

function toInput(b: Brand): BrandInput {
  return {
    name: b.name, kind: b.kind, parent_id: b.parent_id ?? null,
    template_key: b.template_key ?? '', group_key: b.group_key ?? '',
    bg_color: b.bg_color ?? null, text_color: b.text_color ?? null,
    button_color: b.button_color ?? null, button_hover_color: b.button_hover_color ?? null,
    button_text_color: b.button_text_color ?? null,
    button_text_hover_color: b.button_text_hover_color ?? null,
    bg_gradient_from: b.bg_gradient_from ?? null,
    bg_gradient_mid: b.bg_gradient_mid ?? null,
    bg_gradient_to: b.bg_gradient_to ?? null,
    bg_gradient_angle: b.bg_gradient_angle ?? null,
    notes: b.notes ?? '', sort_order: b.sort_order, is_active: b.is_active,
  };
}

// ─── Swatch row ─────────────────────────────────────────────────────────────

const Swatches: React.FC<{ brand: Brand; theme: ScorecardTheme }> = ({ brand, theme }) => {
  const set = COLOR_FIELDS.filter((f) => brand[f.key]);
  const hasGradient = !!(brand.bg_gradient_from && brand.bg_gradient_to);
  if (set.length === 0 && !hasGradient) {
    return (
      <span style={{ fontSize: '0.72rem', color: theme.textMuted, fontStyle: 'italic' }}>
        Inherits defaults
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
      {hasGradient && (
        <span
          title={`Gradient: ${brand.bg_gradient_from}${brand.bg_gradient_mid ? ` → ${brand.bg_gradient_mid}` : ''} → ${brand.bg_gradient_to} @ ${brand.bg_gradient_angle ?? 180}deg`}
          style={{
            width: 34, height: 16, borderRadius: 3,
            background: brandBackground(brand),
            border: '1px solid rgba(0,0,0,0.15)',
          }}
        />
      )}
      {set.map((f) => (
        <span
          key={f.key}
          title={`${f.label}: ${brand[f.key]}`}
          style={{
            width: 16, height: 16, borderRadius: 3,
            background: brand[f.key] as string,
            border: '1px solid rgba(0,0,0,0.15)',
          }}
        />
      ))}
    </div>
  );
};

// ─── Editor ─────────────────────────────────────────────────────────────────

const Editor: React.FC<{
  theme: ScorecardTheme;
  initial: BrandInput;
  brands: Brand[];
  choices: { templates: string[]; groups: string[] };
  editingId: number | null;
  saving: boolean;
  onSave: (b: BrandInput) => void;
  onCancel: () => void;
}> = ({ theme, initial, brands, choices, editingId, saving, onSave, onCancel }) => {
  const [b, setB] = useState<BrandInput>(initial);
  const set = <K extends keyof BrandInput>(k: K, v: BrandInput[K]) => setB((p) => ({ ...p, [k]: v }));

  const label: React.CSSProperties = {
    fontSize: '0.7rem', fontWeight: 600, color: theme.textSecondary,
    display: 'block', marginBottom: '0.25rem',
  };
  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', fontSize: '0.85rem', padding: '0.4rem 0.55rem',
    borderRadius: 4, border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
    color: theme.textPrimary, fontFamily: 'inherit',
  };
  const field = (l: string, node: React.ReactNode) => (<div><label style={label}>{l}</label>{node}</div>);

  // A sub-brand can't parent to itself, and we only allow one level of nesting.
  const parentOptions = brands.filter((x) => x.kind === 'brand' && x.id !== editingId);

  const withCurrent = (options: string[], current?: string | null) =>
    current && !options.includes(current) ? [...options, current].sort() : options;
  const templateOptions = withCurrent(choices.templates, b.template_key);
  const groupOptions = withCurrent(choices.groups, b.group_key);

  // Both stops required, matching the renderer.
  const gradientOn = !!(b.bg_gradient_from && b.bg_gradient_to);

  const valid = b.name.trim() !== '' && (b.kind !== 'sub_brand' || !!b.parent_id);

  return (
    <div style={{
      background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, boxShadow: theme.cardShadow,
      borderRadius: 4, padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
    }}>
      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: theme.textPrimary }}>
        {editingId == null ? 'New brand' : 'Edit brand'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {field('Name', (
          <input style={input} value={b.name} onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Celebration Villa" />
        ))}
        {field('Type', (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <select style={{ ...input, flex: '0 0 45%' }} value={b.kind}
              onChange={(e) => {
                const kind = e.target.value as BrandKind;
                setB((p) => ({ ...p, kind, parent_id: kind === 'brand' ? null : p.parent_id }));
              }}>
              <option value="brand">Brand</option>
              <option value="sub_brand">Sub-brand</option>
            </select>
            {b.kind === 'sub_brand' && (
              <select style={input} value={b.parent_id ?? ''}
                onChange={(e) => set('parent_id', e.target.value ? Number(e.target.value) : null)}>
                <option value="">Parent brand…</option>
                {parentOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {field('Community template (ROT)', (
          <select style={input} value={b.template_key || ''}
            onChange={(e) => set('template_key', e.target.value || null)}>
            <option value="">— none —</option>
            {templateOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        {field('Community group (ROT)', (
          <select style={input} value={b.group_key || ''}
            onChange={(e) => set('group_key', e.target.value || null)}>
            <option value="">— none —</option>
            {groupOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
      </div>
      <p style={{ fontSize: '0.72rem', color: theme.textMuted, margin: '-0.4rem 0 0' }}>
        Template is the join key — a site's banner picks up these colors when its
        community template matches. Choices come from the site registry plus
        templates already in use; leave as none for a brand with no sites of its own.
      </p>

      <div style={{ display: 'flex', gap: '1.1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {COLOR_FIELDS.map((f) => {
          const on = b[f.key] != null;
          return (
            <div key={f.key}>
              <label style={label}>{f.label}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <input
                  type="checkbox"
                  checked={on}
                  title={on ? 'Using a brand color' : 'Inherit the default'}
                  onChange={(e) => set(f.key, e.target.checked ? f.fallback : null)}
                />
                <input
                  type="color"
                  disabled={!on}
                  value={(b[f.key] as string) || f.fallback}
                  onChange={(e) => set(f.key, e.target.value)}
                  style={{
                    width: 44, height: 32, border: 'none', background: 'none',
                    cursor: on ? 'pointer' : 'not-allowed', opacity: on ? 1 : 0.35,
                  }}
                />
              </div>
            </div>
          );
        })}
        <div>
          <label style={label}>Gradient</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <input
              type="checkbox"
              checked={gradientOn}
              title="Use a gradient background instead of a flat colour"
              onChange={(e) => {
                if (e.target.checked) {
                  setB((p) => ({
                    ...p,
                    bg_gradient_from: p.bg_gradient_from || p.bg_color || BANNER_DEFAULTS.bg_color,
                    bg_gradient_to: p.bg_gradient_to || GRADIENT_TO_DEFAULT,
                    bg_gradient_angle: p.bg_gradient_angle ?? 180,
                  }));
                } else {
                  setB((p) => ({
                    ...p, bg_gradient_from: null, bg_gradient_mid: null,
                    bg_gradient_to: null, bg_gradient_angle: null,
                  }));
                }
              }}
            />
            <input type="color" disabled={!gradientOn} title="Gradient start"
              value={b.bg_gradient_from || b.bg_color || BANNER_DEFAULTS.bg_color}
              onChange={(e) => set('bg_gradient_from', e.target.value)}
              style={{ width: 38, height: 32, border: 'none', background: 'none', cursor: gradientOn ? 'pointer' : 'not-allowed', opacity: gradientOn ? 1 : 0.35 }} />
            <select disabled={!gradientOn} value={b.bg_gradient_mid ? '3' : '2'}
              title="Number of gradient colours"
              onChange={(e) => set('bg_gradient_mid',
                e.target.value === '3' ? (b.bg_gradient_mid || GRADIENT_MID_DEFAULT) : null)}
              style={{ ...input, width: 'auto', padding: '0.3rem 0.35rem', opacity: gradientOn ? 1 : 0.35 }}>
              <option value="2">2 colours</option>
              <option value="3">3 colours</option>
            </select>
            {b.bg_gradient_mid && (
              <input type="color" disabled={!gradientOn} title="Gradient middle"
                value={b.bg_gradient_mid}
                onChange={(e) => set('bg_gradient_mid', e.target.value)}
                style={{ width: 38, height: 32, border: 'none', background: 'none', cursor: gradientOn ? 'pointer' : 'not-allowed', opacity: gradientOn ? 1 : 0.35 }} />
            )}
            <input type="color" disabled={!gradientOn} title="Gradient end"
              value={b.bg_gradient_to || GRADIENT_TO_DEFAULT}
              onChange={(e) => set('bg_gradient_to', e.target.value)}
              style={{ width: 38, height: 32, border: 'none', background: 'none', cursor: gradientOn ? 'pointer' : 'not-allowed', opacity: gradientOn ? 1 : 0.35 }} />
            <select disabled={!gradientOn} value={String(b.bg_gradient_angle ?? 180)}
              onChange={(e) => set('bg_gradient_angle', Number(e.target.value))} title="Gradient direction"
              style={{ ...input, width: 'auto', padding: '0.3rem 0.35rem', opacity: gradientOn ? 1 : 0.35 }}>
              <option value="180">↓ top to bottom</option>
              <option value="0">↑ bottom to top</option>
              <option value="90">→ left to right</option>
              <option value="270">← right to left</option>
              <option value="135">↘ diagonal</option>
              <option value="45">↗ diagonal</option>
            </select>
          </div>
        </div>
        <label style={{ fontSize: '0.8rem', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <input type="checkbox" checked={b.is_active}
            onChange={(e) => set('is_active', e.target.checked)} /> Active
        </label>
      </div>
      <p style={{ fontSize: '0.72rem', color: theme.textMuted, margin: '-0.4rem 0 0' }}>
        Unchecked colors inherit — from the parent brand if there is one, otherwise
        the global banner defaults. A gradient needs both stops; with only one set
        the flat background colour is used instead.
      </p>

      {field('Notes', (
        <input style={input} value={b.notes || ''} onChange={(e) => set('notes', e.target.value)}
          placeholder="Optional" />
      ))}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{
          fontSize: '0.8rem', padding: '0.45rem 0.9rem', borderRadius: 4,
          border: `1px solid ${theme.cardBorder}`, background: 'transparent',
          color: theme.textSecondary, cursor: 'pointer', fontFamily: 'inherit',
        }}>Cancel</button>
        <button type="button" disabled={!valid || saving} onClick={() => onSave(b)} style={{
          fontSize: '0.8rem', fontWeight: 700, padding: '0.45rem 1.1rem', borderRadius: 4,
          border: 'none', background: valid && !saving ? theme.accent : theme.cardBorder,
          color: valid && !saving ? '#111' : theme.textMuted,
          cursor: valid && !saving ? 'pointer' : 'default', fontFamily: 'inherit',
        }}>{saving ? 'Saving…' : 'Save brand'}</button>
      </div>
    </div>
  );
};

// ─── View ───────────────────────────────────────────────────────────────────

export const ClientBrandsView: React.FC<ClientBrandsProps> = ({ theme }) => {
  const [client, setClient] = useState<BackendClientId>('plc');
  const [data, setData] = useState<
    { brands: Brand[]; sites_without_template: number; choices: { templates: string[]; groups: string[] } } | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<{ id: number | null; input: BrandInput } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const resp = await fetchBrands(client);
      setData({
        brands: resp.brands,
        sites_without_template: resp.sites_without_template,
        choices: resp.choices ?? { templates: [], groups: [] },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { void load(); }, [load]);

  // Memoized so the derived tree below doesn't rebuild on every render.
  const brands = useMemo(() => data?.brands ?? [], [data]);
  const topLevel = useMemo(() => brands.filter((b) => b.kind === 'brand'), [brands]);
  const subsByParent = useMemo(() => {
    const map = new Map<number, Brand[]>();
    for (const b of brands) {
      if (b.kind === 'sub_brand' && b.parent_id != null) {
        map.set(b.parent_id, [...(map.get(b.parent_id) ?? []), b]);
      }
    }
    return map;
  }, [brands]);
  // A sub-brand whose parent was deleted (FK sets parent_id NULL) would
  // otherwise vanish from the tree — surface it rather than hide it.
  const orphans = useMemo(
    () => brands.filter((b) => b.kind === 'sub_brand' && b.parent_id == null),
    [brands],
  );

  const save = async (input: BrandInput) => {
    if (!editing) return;
    setSaving(true); setError(null);
    try {
      const payload: BrandInput = {
        ...input,
        template_key: input.template_key?.trim() || null,
        group_key: input.group_key?.trim() || null,
        notes: input.notes?.trim() || null,
      };
      if (editing.id == null) await createBrand(client, payload);
      else await updateBrand(client, editing.id, payload);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b: Brand) => {
    const subs = subsByParent.get(b.id)?.length ?? 0;
    const extra = subs ? `\n\n${subs} sub-brand(s) will be kept but left without a parent.` : '';
    if (!window.confirm(`Delete brand "${b.name}"?${extra}`)) return;
    setError(null);
    try {
      await deleteBrand(client, b.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const smallBtn = (color: string): React.CSSProperties => ({
    fontSize: '0.72rem', fontWeight: 600, padding: '0.28rem 0.55rem', borderRadius: 4,
    border: `1px solid ${theme.cardBorder}`, background: 'transparent', color,
    cursor: 'pointer', fontFamily: 'inherit',
  });

  const row = (b: Brand, isSub: boolean) => (
    <div
      key={b.id}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        padding: '0.6rem 0.75rem', borderTop: `1px solid ${theme.rowDivider}`,
        paddingLeft: isSub ? '2.25rem' : '0.75rem',
        opacity: b.is_active ? 1 : 0.55,
      }}
    >
      <div style={{ minWidth: 180, flex: '1 1 200px' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.textPrimary }}>
          {isSub ? '↳ ' : ''}{b.name}
          {!b.is_active && (
            <span style={{ fontSize: '0.68rem', color: theme.textMuted, marginLeft: '0.4rem' }}>
              (inactive)
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>
          {b.template_key ? `template: ${b.template_key}` : 'no template'}
          {b.group_key ? ` · group: ${b.group_key}` : ''}
        </div>
      </div>
      <div style={{ fontSize: '0.75rem', color: theme.textSecondary, minWidth: 90 }}>
        {b.site_count} live site{b.site_count === 1 ? '' : 's'}
      </div>
      <Swatches brand={b} theme={theme} />
      <div style={{ display: 'flex', gap: '0.4rem', marginLeft: 'auto' }}>
        {!isSub && (
          <button type="button" style={smallBtn(theme.textSecondary)}
            onClick={() => setEditing({ id: null, input: emptyBrand('sub_brand', b.id) })}>
            + Sub-brand
          </button>
        )}
        <button type="button" style={smallBtn(theme.textSecondary)}
          onClick={() => setEditing({ id: b.id, input: toInput(b) })}>
          Edit
        </button>
        <button type="button" style={smallBtn('#dc2626')} onClick={() => void remove(b)}>
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.textPrimary, margin: 0 }}>
            Client Brands
          </h2>
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            Brands and sub-brands per client, with the colors banners use as presets.
            {data && <> · {topLevel.length} brand{topLevel.length === 1 ? '' : 's'} · {brands.length} total</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            value={client}
            onChange={(e) => { setClient(e.target.value as BackendClientId); setEditing(null); }}
            style={{
              fontSize: '0.82rem', padding: '0.35rem 0.55rem', borderRadius: 4,
              border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
              color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            {BRAND_CLIENTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing({ id: null, input: emptyBrand('brand', null) })}
              style={{
                fontSize: '0.8rem', fontWeight: 700, padding: '0.4rem 0.9rem', borderRadius: 4,
                border: 'none', background: theme.accent, color: '#111', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              + Add brand
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)',
          borderRadius: 4, padding: '0.7rem 0.9rem', fontSize: '0.82rem', color: '#991b1b',
        }}>
          {error}
        </div>
      )}

      {editing && (
        <Editor
          theme={theme}
          initial={editing.input}
          brands={brands}
          choices={data?.choices ?? { templates: [], groups: [] }}
          editingId={editing.id}
          saving={saving}
          onSave={(b) => void save(b)}
          onCancel={() => setEditing(null)}
        />
      )}

      {loading && (
        <p style={{ fontSize: '0.85rem', color: theme.textMuted }}>Loading brands…</p>
      )}

      {!loading && brands.length === 0 && !error && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          boxShadow: theme.cardShadow, borderRadius: 4, padding: '2rem',
          textAlign: 'center', fontSize: '0.85rem', color: theme.textSecondary,
        }}>
          No brands yet for this client. Add one, or run
          {' '}<code>scripts/import_plc_rot.py</code>{' '}to seed them from the ROT.
        </div>
      )}

      {!loading && brands.length > 0 && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          boxShadow: theme.cardShadow, borderRadius: 4, overflow: 'hidden',
        }}>
          {topLevel.map((b) => (
            <React.Fragment key={b.id}>
              {row(b, false)}
              {(subsByParent.get(b.id) ?? []).map((s) => row(s, true))}
            </React.Fragment>
          ))}
          {orphans.map((s) => row(s, true))}
        </div>
      )}

      {!loading && data && data.sites_without_template > 0 && (
        <p style={{ fontSize: '0.78rem', color: theme.textMuted }}>
          {data.sites_without_template} registered site
          {data.sites_without_template === 1 ? '' : 's'} have no community template set,
          so their banners fall back to the global defaults.
        </p>
      )}
    </div>
  );
};

export default ClientBrandsView;
